import type { ActionContract } from './flowSchema';

/**
 * Outcomes tipados de cada action registrada. validateFlow exige que el `next`
 * de todo nodo action cubra EXACTAMENTE estos outcomes; el runtime registra el
 * componente React bajo el mismo nombre. Agregar una action = una entrada aquí
 * + un componente en el registry del runtime — nada más.
 */
export const ACTION_CONTRACTS = {
	/** Captura del número + verificación del código en UN nodo — la EXCEPCIÓN
	 * deliberada a "un action pinta una pantalla" (la regla general vive en el
	 * registry del runtime). El reto del OTP es efímero de sesión: si el código
	 * fuera nodo propio, un reload resumiría en una pantalla de código huérfana
	 * (sin reto en vuelo). Por eso captura y verificación son estados INTERNOS y
	 * el resume siempre re-asienta en la captura del número. */
	// El único outcome es 'verified' aunque el experimento
	// phone-verification-optional (ctx.phoneOptional) guarde el número sin OTP:
	// para el flow el desenlace es el mismo — hay celular estampado. La
	// diferencia (verificado o no) vive server-side en cellphone_verified.
	//
	// 'verified' es reconsiderable a propósito: el teléfono del PERFIL es la
	// verdad, y puede desaparecer después de grabado el outcome (el guard del
	// engine desarma claims sin verificar cuando el dueño real de la línea la
	// prueba — ADR 006 de heru-user-management-engine). Con el outcome grabado
	// para siempre, la re-entrada auto-avanzaba el nodo y aterrizaba en goals
	// mientras el resolver seguía exigiendo teléfono: loop sin salida. El
	// olvido en hidratación + visibleWhen (hasPhone eq false) dan la condición
	// real: con teléfono en el perfil el nodo ni se muestra; sin teléfono,
	// vuelve a frenar ahí y captura uno nuevo.
	'phone-verification': { outcomes: ['verified'], reconsiderable: ['verified'] },
	/** Captura del correo + verificación (Google preferente, OTP de respaldo) en
	 * UN nodo, por la MISMA razón que 'phone-verification': el reto es efímero de
	 * sesión y el resume debe re-asentar en la captura. Verificar es obligatorio
	 * —un correo sin verificar es de dónde salen las cuentas duplicadas—, así que
	 * el único outcome es 'verified'; el correo tomado no es un desenlace del
	 * nodo sino el merge, que re-autentica y sale del flow. */
	/** Vinculación SAT = TRES pantallas, cada una su nodo (paridad con el
	 * LinkingFlow legacy full, que las escondía en estados internos):
	 * el picker de método, y un formulario por método. La verdad del dominio es
	 * asíncrona (v2 SATLinkingView): el submit aceptado dispara un workflow cuyo
	 * polling sigue en segundo plano, así que el único outcome de los formularios
	 * es 'submitted'. Un rechazo de credenciales se reintenta en la misma
	 * pantalla (banner de error, no es outcome), "lo haré después" es el skip
	 * nativo, y la tarjeta "continúa en segundo plano" es un nodo info del flow.
	 * Vinculado = ctx.isLinked (los nodos se ocultan con visibleWhen). */
	'sat-method': { outcomes: ['efirma', 'ciec'] },
	/** La vinculación NO es irreversible: el workflow puede FALLAR en segundo
	 * plano y el usuario debe poder volver al formulario a reintentar. Su
	 * inaccesibilidad mientras corre es condición viva: visibleWhen sobre
	 * ctx.isLinked/isLinking (el host la baja al fallar). */
	'sat-ciec': { outcomes: ['submitted'] },
	'sat-efirma': { outcomes: ['submitted'] },
	/** El cobro (purchased) es el hecho irreversible. 'declined' vale para UNA
	 * pasada: no consume el nodo (walk.isConsumed exceptúa reconsiderables, el
	 * back lo alcanza) y la hidratación lo olvida (hostState) — el plan se
	 * vuelve a ofrecer en todo resume; solo purchased lo cierra. */
	purchase: {
		outcomes: ['purchased', 'declined'],
		reconsiderable: ['declined'],
	},
	/** Agendar asesoría fiscal (embed Cal.com self-hosted). Con una cita ya
	 * existente (ctx.hasBooking) el interior pinta el estado agendado en
	 * vez del calendario — regresar al nodo con cita vigente ES ver ese estado,
	 * y si la cita se cancela afuera el calendario reaparece; por eso tampoco
	 * es irreversible. Ambos caminos confirman con 'scheduled'. */
	'advisory-booking': { outcomes: ['scheduled'] },
	/** Constructor de paquete MANUAL: el usuario declara qué meses debe (sin
	 * data del SAT). Gemelo excluyente de 'linked-period-selection' por
	 * visibleWhen sobre ctx.isLinked/isSyncing. */
	'period-selection': { outcomes: ['selected'] },
	/** Constructor de paquete VINCULADO: los periodos llegan con su estado SAT
	 * (pendiente/declarado/cubierto) desde el catálogo del puerto y solo los
	 * pendientes son seleccionables. 'none_pending' = al día (nada que
	 * regularizar): el flow SALE a home — nunca cruza el paquete ni la
	 * confirmación de compra. */
	'linked-period-selection': { outcomes: ['selected', 'none_pending'] },
	/** Cotización SIN vincular (bundle 3.2): el host arma el adeudo con lo que
	 * el usuario declaró —régimen + cuánto lleva sin presentar— y monta la
	 * MISMA pantalla de oferta que el modal de declaraciones, como chrome del
	 * host (mismo precedente que los uploaders de credenciales). Reusarla es lo
	 * que garantiza que las dos superficies no divergan en copy ni en precio:
	 * si divergieran, el experimento mediría la diferencia entre dos pantallas
	 * y no entre dos brazos.
	 *
	 * 'declined' cae al flujo de siempre (vincular o armar el paquete a mano),
	 * así que rechazar la oferta no es un callejón. */
	'declared-quote': {
		outcomes: ['purchased', 'declined'],
		reconsiderable: ['declined'],
	},
	/** Desglose del paquete (Tu paquete) antes del plan. */
	'package-review': { outcomes: ['continue'] },
	/** El chat de análisis IA (AnalysisView de v2): el brief personalizado
	 * generado con las respuestas del flow. Flow-aware por el edge del nodo:
	 * en diagnóstico su continue es terminal (→ dashboard), en contabilidad
	 * avanza al plan. Fase 1: la regeneración real del análisis la dispara el
	 * host (useRegenerateAnalysis); la pantalla pura pinta el brief
	 * representativo. */
	'analysis-chat': { outcomes: ['continue'] },
	/** Confirmación de "solo quiero explorar". Su único outcome SALE del
	 * onboarding sin completar (`exits`): el host estampa presencia 'exited' y
	 * navega a home — explorar NO es completar. El `next` queda vacío (el outcome
	 * es puerta de salida, no un edge) y su outcome jamás se persiste; al volver,
	 * la respuesta transient de goals se olvida y el usuario re-elige. El
	 * "Continuar mi configuración" de v2 regresaba a goals — en v3 eso ES el back
	 * del runner (un edge de regreso sería un ciclo en el DAG), la pantalla lo
	 * señala como hint. */
	'exploring-confirm': { outcomes: ['explore'], exits: ['explore'] },
	/** Términos y Condiciones heru × aliado (Anexo del aliado en compliance):
	 * aceptar autoriza compartir la información fiscal con el aliado del flow.
	 * El documento que se firma lo resuelve el catálogo (Ally.compliance.docType)
	 * — la pantalla es UNA, parametrizada por aliado. Reversible: re-aceptar es
	 * idempotente (el gate del host solo registra la PRIMERA aceptación). */
	'ally-tcs': { outcomes: ['accepted'] },
	/** Hub de cumplimiento fiscal del aliado: requisitos accionables (CURP si el
	 * aliado lo exige, e.firma, CSD), verificaciones derivadas de ctx y plan
	 * recomendado embebido. 'exit' es el Salir del hub; 'plan' es el hito del
	 * plan de contabilidad, que NO cobra in-place: entra a la cola estándar de
	 * venta del aliado (capacidades → plan → compra), igual que prod. */
	'ally-profile': { outcomes: ['exit', 'plan'] },
	/** ALIAS CONGELADOS de 2.5 (el flow congelado nombra las actions con el
	 * prefijo del aliado original; no se reescribe — ver flows/index). Renderizan
	 * las MISMAS pantallas generalizadas que 'ally-tcs'/'ally-profile'. */
	'zubale-tcs': { outcomes: ['accepted'] },
	/** Parking del Zubalero NO inscrito (paridad v2): requisitos para operar y
	 * CTA externo a zubale.com — sin outcome propio; se sale con back
	 * (reconsiderar la inscripción) o skip (a home). Paso propio del producto
	 * Zubale (la inscripción es SU alta): no se generaliza sin otro consumidor. */
	'zubale-enrollment': { outcomes: [] },
	'zubale-profile': { outcomes: ['exit', 'plan'] },
	/** Capa de capacidades del aliado (Pieza A): "todo lo que heru hace por ti
	 * como {rol}" con el content pack del catálogo. Reversible (solo vende): su
	 * único outcome 'continue' avanza al plan. Se oculta con plan activo
	 * (visibleWhen sobre ctx.hasActivePlan), igual que el plan que le sigue. */
	'ally-capabilities': { outcomes: ['continue'] },
	/** Plan del aliado (Pieza B). SIEMPRE se pinta: con plan activo no se oculta,
	 * se marca activo — el estado del plan es la verdad de ctx.hasActivePlan, no
	 * un nodo aparte. Por eso hay dos outcomes hacia el mismo cierre: 'purchased'
	 * (compró aquí; el éxito lo pinta el drawer, no un nodo) y 'continue' (ya lo
	 * tenía y avanza desde el estado activo) — sin el segundo, el plan activo sería
	 * una pantalla sin salida. El skip del nodo es el "Omitir" de v2.
	 *
	 * NO es irreversible, a diferencia de 'purchase': un action irreversible deja
	 * de ser aterrizable en cuanto grabó outcome, así que el nodo se atravesaría
	 * por su 'purchased' viejo y la pantalla del plan no volvería a pintarse jamás
	 * — justo lo contrario de "siempre se ve el plan". El doble cobro no lo impide
	 * el consumo del nodo sino ctx.hasActivePlan: con plan activo la pantalla no
	 * ofrece cobro, ofrece continuar. */
	'ally-plan': { outcomes: ['purchased', 'continue'] },
	/** The seals (CSD) trámite of the invoicing-only branch: a told wait with
	 * its price, then the ASAT checkout, and closing it is the outcome,
	 * 'purchased' or 'dismissed'.
	 * Both finish the onboarding on the invoicing exit: who does not pay still
	 * lands on invoicing, whose seals callout keeps offering the upload and the
	 * in-house trámite. Not reconsiderable: the node is crossed once, on the way
	 * out. */
	'seals-asat': { outcomes: ['purchased', 'dismissed'] },
	/** The seals (CSD) upload of the invoicing-only branch, as a step of the
	 * onboarding: the e.firma linking form with the seals' copy, backed by
	 * invoicing's own checks and upload (files, password and, when the server
	 * asks, the issuer's regime and postal code). 'registered' = the seal is
	 * on the account, and the flow finishes on invoicing. 'asat' = the password
	 * is lost and the user takes «heru te ayuda a tramitar unos nuevos», the
	 * seals trámite. Leaving without seals is the node's skip: the files often
	 * live on another device, and invoicing's seals callout keeps offering the
	 * upload. */
	'seals-upload': { outcomes: ['registered', 'asat'] },
	/** The invoicing plan offer of the invoicing-only branch, once the seals
	 * are on the account: the plan its `inv_volume` answer points to, monthly
	 * by default, sold through the onboarding drawer. 'purchased' = paid;
	 * 'free' = stays on the free plan (5 invoices a month), which is the
	 * screen's own CTA and why the node has no skip. Closing the drawer
	 * without paying is no outcome: the offer stays, Gratis included. Both
	 * outcomes finish on the invoicing exit. Not reconsiderable: the node is
	 * crossed once on the way out, and whoever leaves on Gratis buys later
	 * from /facturacion/planes. */
	'invoicing-plan': { outcomes: ['purchased', 'free'] },
} as const satisfies Record<string, ActionContract>;

/** Union cerrado de actions: el registry de componentes se tipa
 * Record<ActionName, …> y el compilador exige paridad en ambas direcciones. */
export type ActionName = keyof typeof ACTION_CONTRACTS;
